import { t } from '../../../common/i18n.js'
import { Language } from '../../../common/types/i18n.js'
import { UrlLinkBrand, UrlLinkSelect } from '../../../server/database/types.js'
import { PageHeader } from '../../components/page-header/page-header.js'
import { PageShell } from '../../components/page-shell/page-shell.js'

export interface UrlShortenerViewProps {
    lang: Language
    links: UrlLinkSelect[]
    isLoading: boolean
    showCreateForm: boolean
    isCreating: boolean
    onToggleCreateForm: () => void
    onCreate: (data: { original_url: string; title: string; brand: UrlLinkBrand }) => void
    onDelete: (id: number) => void
    onViewDetail: (id: number) => void
    onCopyLink: (shortUrl: string) => void
    getShortUrl: (link: UrlLinkSelect) => string
}

const BRAND_DOMAINS: Record<UrlLinkBrand, string> = {
    play: 'url.play.ee',
    nope: 'url.nope.ee',
}

interface CreateFormState {
    original_url: string
    title: string
    brand: UrlLinkBrand
}

export const UrlShortenerView = ({
    lang,
    links,
    isLoading,
    showCreateForm,
    isCreating,
    onToggleCreateForm,
    onCreate,
    onDelete,
    onViewDetail,
    onCopyLink,
    getShortUrl,
}: UrlShortenerViewProps) => {
    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        const form = e.currentTarget
        const data = Object.fromEntries(
            new FormData(form) as unknown as Iterable<readonly [PropertyKey, string]>,
        ) as unknown as CreateFormState
        if (!data.original_url) return
        onCreate({ ...data, brand: data.brand ?? 'play' })
    }

    return (
        <PageShell>
            <PageHeader
                title={t.urlShortener.pageTitle(lang)}
                subtitle={t.urlShortener.pageSubtitle(lang)}
                actions={
                    <button
                        className="url-shortener__create-btn"
                        onClick={onToggleCreateForm}
                        type="button"
                    >
                        {showCreateForm ? t.common.cancel(lang) : t.urlShortener.newLink(lang)}
                    </button>
                }
            />

            {showCreateForm && (
                <form className="url-shortener__create-form" onSubmit={handleSubmit}>
                    <div className="url-shortener__form-row">
                        <div className="url-shortener__form-field">
                            <label className="url-shortener__label">
                                {t.urlShortener.originalUrl(lang)} *
                            </label>
                            <input
                                className="url-shortener__input"
                                type="url"
                                name="original_url"
                                placeholder={t.urlShortener.originalUrlPlaceholder(lang)}
                                required
                            />
                        </div>
                        <div className="url-shortener__form-field">
                            <label className="url-shortener__label">
                                {t.urlShortener.title(lang)}
                            </label>
                            <input
                                className="url-shortener__input"
                                type="text"
                                name="title"
                                placeholder={t.urlShortener.titlePlaceholder(lang)}
                                maxLength={255}
                            />
                        </div>
                        <div className="url-shortener__form-field url-shortener__form-field--narrow">
                            <label className="url-shortener__label">
                                {t.urlShortener.brand(lang)}
                            </label>
                            <select
                                className="url-shortener__select"
                                name="brand"
                                defaultValue="play"
                            >
                                <option value="play">play</option>
                                <option value="nope">nope</option>
                            </select>
                        </div>
                        <button
                            className="url-shortener__submit-btn"
                            type="submit"
                            disabled={isCreating}
                        >
                            {isCreating
                                ? t.urlShortener.creating(lang)
                                : t.urlShortener.createLink(lang)}
                        </button>
                    </div>
                </form>
            )}

            {isLoading && !links.length ? (
                <div className="url-shortener__loading">…</div>
            ) : links.length === 0 ? (
                <div className="url-shortener__empty">{t.urlShortener.noLinks(lang)}</div>
            ) : (
                <table className="url-shortener__table">
                    <thead>
                        <tr>
                            <th>{t.urlShortener.brand(lang)}</th>
                            <th>{t.urlShortener.shortUrl(lang)}</th>
                            <th>{t.urlShortener.title(lang)}</th>
                            <th>{t.urlShortener.hits(lang)}</th>
                            <th>{t.urlShortener.created(lang)}</th>
                            <th>{t.urlShortener.lastAccessed(lang)}</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {links.map((link) => (
                            <UrlLinkRow
                                key={link.id}
                                lang={lang}
                                link={link}
                                shortUrl={getShortUrl(link)}
                                onDelete={onDelete}
                                onViewDetail={onViewDetail}
                                onCopyLink={onCopyLink}
                            />
                        ))}
                    </tbody>
                </table>
            )}
        </PageShell>
    )
}

interface UrlLinkRowProps {
    lang: Language
    link: UrlLinkSelect
    shortUrl: string
    onDelete: (id: number) => void
    onViewDetail: (id: number) => void
    onCopyLink: (shortUrl: string) => void
}

const UrlLinkRow = ({
    lang,
    link,
    shortUrl,
    onDelete,
    onViewDetail,
    onCopyLink,
}: UrlLinkRowProps) => {
    const domain = BRAND_DOMAINS[link.brand]
    const fullShortUrl = `https://${domain}/${link.short_code}`
    const createdDate = link.created_at_utc.slice(0, 10)
    const lastAccessed = link.last_accessed_at_utc
        ? link.last_accessed_at_utc.slice(0, 10)
        : t.urlShortener.never(lang)

    return (
        <tr className="url-shortener__row">
            <td>
                <span
                    className={`url-shortener__brand-badge url-shortener__brand-badge--${link.brand}`}
                >
                    {link.brand}
                </span>
            </td>
            <td>
                <a
                    className="url-shortener__short-link"
                    href={fullShortUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {shortUrl}
                </a>
            </td>
            <td className="url-shortener__original-url" title={link.original_url}>
                {link.title ?? link.original_url}
            </td>
            <td>{link.hits}</td>
            <td>{createdDate}</td>
            <td>{lastAccessed}</td>
            <td className="url-shortener__actions">
                <button
                    className="url-shortener__action-btn"
                    onClick={() => onCopyLink(fullShortUrl)}
                    type="button"
                    title={t.urlShortener.copyLink(lang)}
                >
                    {t.urlShortener.copyLink(lang)}
                </button>
                <button
                    className="url-shortener__action-btn"
                    onClick={() => onViewDetail(link.id)}
                    type="button"
                    title={t.urlShortener.viewDetails(lang)}
                >
                    QR / {t.urlShortener.stats(lang)}
                </button>
                <button
                    className="url-shortener__action-btn url-shortener__action-btn--danger"
                    onClick={() => {
                        if (window.confirm(t.urlShortener.confirmDelete(lang))) {
                            onDelete(link.id)
                        }
                    }}
                    type="button"
                    title={t.urlShortener.deleteLink(lang)}
                >
                    {t.common.delete(lang)}
                </button>
            </td>
        </tr>
    )
}
